import {
  AccountDeletionBlockers,
  AccountDeletionRequest,
  AccountDeletionStatus,
  FetchAccountDeletionRequestsArgs,
} from '../../types/accountDeletion';
import { formatDateTime } from '../../utils/date';

type QueueFilters = Omit<FetchAccountDeletionRequestsArgs, 'offset' | 'limit'>;

/** The queue's tabs, the Ops work queue first. Each maps to the only filters madkhol-user accepts. */
export const QUEUE_VIEWS: { key: string; label: string; args: QueueFilters }[] = [
  { key: 'ready', label: 'Ready to start', args: { readyOnly: true } },
  { key: 'processing', label: 'Processing', args: { status: AccountDeletionStatus.PROCESSING } },
  { key: 'submitted', label: 'Submitted', args: { status: AccountDeletionStatus.SUBMITTED } },
  { key: 'completed', label: 'Completed', args: { status: AccountDeletionStatus.COMPLETED } },
  { key: 'cancelled', label: 'Cancelled', args: { status: AccountDeletionStatus.CANCELLED } },
  { key: 'all', label: 'All', args: {} },
];

const isReviewOver = (request: AccountDeletionRequest, now: Date) => new Date(request.reviewEndsAt) <= now;

export interface DeletionActions {
  cancel: boolean;
  start: boolean;
  /** Why start is shown but disabled; null when it is available or not offered at all. */
  startBlockedReason: string | null;
  complete: boolean;
}

/**
 * What the employee may do with a request now. madkhol-user enforces every rule again; this only keeps the screen from
 * offering an action that would be refused. Cancel needs `update`; start and complete, which delete the account, need
 * `delete`.
 */
export function availableActions(
  request: AccountDeletionRequest,
  now: Date,
  { canCancel, canProcess }: { canCancel: boolean; canProcess: boolean },
): DeletionActions {
  const submitted = request.status === AccountDeletionStatus.SUBMITTED;
  const reviewOver = isReviewOver(request, now);

  return {
    cancel: submitted && canCancel,
    start: submitted && canProcess && reviewOver,
    // The server sets the review length; its end date is the only figure the screen shows.
    startBlockedReason:
      submitted && canProcess && !reviewOver ? `The review ends ${formatDateTime(request.reviewEndsAt)}.` : null,
    complete: request.status === AccountDeletionStatus.PROCESSING && canProcess,
  };
}

/**
 * Why Start must wait for the live checks: madkhol-user refuses a start with anything left to clear, so the button
 * stays disabled while they load, when they could not be made, and until every one is clear.
 */
export function startCheckReason(checks: {
  isFetching: boolean;
  isError: boolean;
  canDelete?: boolean;
}): string | null {
  if (checks.isFetching) {
    return 'Checking what the user still has…';
  }

  if (checks.isError) {
    return 'The checks could not be made. Check again before starting.';
  }

  return checks.canDelete ? null : 'The user still has something to clear.';
}

/** Time left in the review, in whole hours and minutes (rounded up), or that it is over. */
export function reviewCountdown(reviewEndsAt: string, now: Date): string {
  const minutesLeft = Math.ceil((new Date(reviewEndsAt).getTime() - now.getTime()) / 60000);

  if (minutesLeft <= 0) {
    return 'Review over';
  }

  return `Ends in ${Math.floor(minutesLeft / 60)}h ${minutesLeft % 60}m`;
}

type ChipColor = 'default' | 'info' | 'warning' | 'secondary' | 'success';

/** A SUBMITTED request reads as "In review" or "Ready to start", which is what Ops acts on. */
export function statusInfo(request: AccountDeletionRequest, now: Date): { label: string; color: ChipColor } {
  switch (request.status) {
    case AccountDeletionStatus.SUBMITTED:
      return isReviewOver(request, now)
        ? { label: 'Ready to start', color: 'warning' }
        : { label: 'In review', color: 'info' };
    case AccountDeletionStatus.PROCESSING:
      return { label: 'Processing', color: 'secondary' };
    case AccountDeletionStatus.COMPLETED:
      return { label: 'Completed', color: 'success' };
    default:
      return { label: 'Cancelled', color: 'default' };
  }
}

const BLOCKERS: { key: keyof AccountDeletionBlockers; label: string; clear: string }[] = [
  {
    key: 'activeInvestments',
    label: 'Active investments',
    clear: 'The user redeems every portfolio (retail and Ratbi+).',
  },
  { key: 'cashBalance', label: 'Cash balance', clear: 'The user withdraws the wallet, or settles a negative balance.' },
  {
    key: 'pendingTransactions',
    label: 'Pending transactions',
    clear: 'A buy, sell or withdrawal is still in flight: wait for it to finish.',
  },
  { key: 'ratbiPlus', label: 'Ratbi+ enrolment', clear: 'The user is enrolled in an employer plan: unlink it first.' },
];

/** The four checks in a fixed order, each with whether it blocks and what clears it. */
export function blockerList(blockers: AccountDeletionBlockers) {
  return BLOCKERS.map(({ key, label, clear }) => ({ key, label, clear, blocked: blockers[key] }));
}

const REFUSALS: Record<string, string> = {
  ACCOUNT_DELETION_NOT_READY: 'The review is not over yet.',
  ACCOUNT_DELETION_NOT_PENDING: 'This request changed status meanwhile. Refresh the list.',
  ACCOUNT_DELETION_BLOCKED: 'The user still has something to clear. See the checks above.',
  ACCOUNT_DELETION_CHECK_UNAVAILABLE: 'A check could not be made right now. Try again in a moment.',
  ACCOUNT_DELETION_NOT_FOUND: 'This request no longer exists.',
};

/** What to tell the employee for a refused action: a known code in plain words, else the server's message. */
export function refusalMessage(code?: string): string {
  if (!code) {
    return 'Something went wrong. Try again.';
  }

  return REFUSALS[code] || code;
}
